import React, {useEffect, useState} from 'react';
import {
    Card, 
    Row, 
    Col, 
    Button, 
    Space, 
    message, 
    Select, 
    Divider,
    Typography,
    Tag
} from 'antd';
import {PrinterOutlined, DownloadOutlined, ArrowLeftOutlined, QrcodeOutlined} from '@ant-design/icons';
import {useApi} from '../provider/ApiProvider';
import {useHistory, useParams} from 'react-router-dom';
import LayoutComponent from '../components/LayoutComponent';
import QRCode from 'react-qr-code';

const {Option} = Select;
const {Title, Text} = Typography;

const TargetLocationQrPrintPage = () => {
    const {api} = useApi();
    const history = useHistory();
    const {id} = useParams();
    const [targetLocation, setTargetLocation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedDoors, setSelectedDoors] = useState([]);
    const [printMode, setPrintMode] = useState(false);

    useEffect(() => {
        if (id) {
            fetchTargetLocation();
        }
    }, [id]);

    const fetchTargetLocation = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/targetLocationRouter/get/${id}`);
            if (response.status) {
                setTargetLocation(response.data);
                // Varsayılan olarak tüm aktif kapıları seç
                const activeDoors = response.data.doors?.filter(door => door.isActive) || [];
                setSelectedDoors(activeDoors.map(door => door.id));
            }
        } catch (error) {
            message.error('Hedef lokasyon yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        if (selectedDoors.length === 0) {
            message.warning('Lütfen en az bir kapı seçin');
            return;
        }
        setPrintMode(true);
        setTimeout(() => {
            window.print();
            setPrintMode(false);
        }, 100);
    };

    const handleSelectAll = () => {
        const allDoorIds = targetLocation?.doors?.map(door => door.id) || [];
        setSelectedDoors(allDoorIds);
    };

    const handleDeselectAll = () => {
        setSelectedDoors([]);
    };

    const handleDoorSelection = (doorId, checked) => {
        if (checked) {
            setSelectedDoors([...selectedDoors, doorId]);
        } else {
            setSelectedDoors(selectedDoors.filter(id => id !== doorId));
        }
    };

    const getSelectedDoors = () => {
        return targetLocation?.doors?.filter(door => selectedDoors.includes(door.id)) || [];
    };

    const QRCodeCard = ({door}) => (
        <Card 
            size="small" 
            style={{ 
                width: 200, 
                margin: '10px',
                textAlign: 'center',
                pageBreakInside: 'avoid',
                breakInside: 'avoid'
            }}
        >
            <div style={{ marginBottom: 8 }}>
                <Title level={5} style={{ margin: 0 }}>{targetLocation.name}</Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>{door.name}</Text>
            </div>
            
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                margin: '10px 0',
                padding: '10px',
                border: '1px solid #d9d9d9',
                borderRadius: '4px'
            }}>
                <QRCode 
                    value={door.qr} 
                    size={120}
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    viewBox={`0 0 120 120`}
                />
            </div>
            
            <div style={{ marginTop: 8 }}>
                <Tag color={door.type === 'entrance' ? 'green' : 'red'}>
                    {door.type === 'entrance' ? 'Giriş' : 'Çıkış'}
                </Tag>
                <div style={{ fontSize: '12px', marginTop: 4 }}>
                    QR: {door.qr}
                </div>
            </div>
        </Card>
    );

    if (!targetLocation) {
        return (
            <LayoutComponent>
                <div style={{ padding: '24px' }}>
                    <Card loading={loading}>
                        <div>Yükleniyor...</div>
                    </Card>
                </div>
            </LayoutComponent>
        );
    }

    if (printMode) {
        return (
            <div className="qr-print-page" style={{ padding: '20px' }}>
                <div className="print-header" style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <Title level={3}>{targetLocation.name} - QR Kodları</Title>
                    <Text type="secondary">Yazdırma Tarihi: {new Date().toLocaleDateString('tr-TR')}</Text>
                </div>
                
                <Row gutter={[16, 16]} justify="center">
                    {getSelectedDoors().map(door => (
                        <Col key={door.id}>
                            <QRCodeCard door={door} />
                        </Col>
                    ))}
                </Row>
            </div>
        );
    }

    return (
        <LayoutComponent>
            <div className="qr-print-page" style={{ padding: '24px' }}>
                <Card 
                    title={
                        <Space>
                            <QrcodeOutlined />
                            <span>QR Kod Yazdırma - {targetLocation.name}</span>
                        </Space>
                    }
                    extra={
                        <Space>
                            <Button 
                                icon={<ArrowLeftOutlined />} 
                                onClick={() => history.push(`/target-locations/view/${id}`)}
                            >
                                Geri Dön
                            </Button>
                        </Space>
                    }
                >
                    <div className="print-controls" style={{ marginBottom: '20px' }}>
                        <Row gutter={16} align="middle">
                            <Col>
                                <Text strong>Kapı Seçimi:</Text>
                            </Col>
                            <Col>
                                <Button size="small" onClick={handleSelectAll}>
                                    Tümünü Seç
                                </Button>
                            </Col>
                            <Col>
                                <Button size="small" onClick={handleDeselectAll}>
                                    Seçimi Kaldır
                                </Button>
                            </Col>
                            <Col>
                                <Text type="secondary">
                                    {selectedDoors.length} kapı seçildi
                                </Text>
                            </Col>
                        </Row>
                    </div>

                    <Divider />

                    <Row gutter={[16, 16]}>
                        {targetLocation.doors?.map(door => (
                            <Col key={door.id} xs={24} sm={12} md={8} lg={6}>
                                <Card 
                                    size="small"
                                    className={`qr-card ${selectedDoors.includes(door.id) ? 'selected' : ''}`}
                                    style={{ 
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => handleDoorSelection(door.id, !selectedDoors.includes(door.id))}
                                >
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ marginBottom: 8 }}>
                                            <Text strong>{door.name}</Text>
                                        </div>
                                        
                                        <div className="qr-code-container" style={{ 
                                            display: 'flex', 
                                            justifyContent: 'center', 
                                            margin: '10px 0'
                                        }}>
                                            <QRCode 
                                                value={door.qr} 
                                                size={80}
                                                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                                                viewBox={`0 0 80 80`}
                                            />
                                        </div>
                                        
                                        <div>
                                            <Tag color={door.type === 'entrance' ? 'green' : 'red'}>
                                                {door.type === 'entrance' ? 'Giriş' : 'Çıkış'}
                                            </Tag>
                                            <div style={{ fontSize: '10px', marginTop: 4, color: '#666' }}>
                                                {door.qr}
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    <Divider />

                    <div style={{ textAlign: 'center' }}>
                        <Space size="large">
                            <Button 
                                type="primary" 
                                size="large"
                                icon={<PrinterOutlined />}
                                onClick={handlePrint}
                                disabled={selectedDoors.length === 0}
                            >
                                QR Kodları Yazdır ({selectedDoors.length})
                            </Button>
                            
                            <Button 
                                size="large"
                                icon={<DownloadOutlined />}
                                onClick={() => {
                                    message.info('PDF indirme özelliği yakında eklenecek');
                                }}
                                disabled={selectedDoors.length === 0}
                            >
                                PDF İndir
                            </Button>
                        </Space>
                    </div>

                    <div className="print-tips" style={{ marginTop: '20px' }}>
                        <Text type="secondary">
                            <strong>Yazdırma İpuçları:</strong>
                            <ul style={{ marginTop: 8, marginBottom: 0 }}>
                                <li>Seçtiğiniz kapıların QR kodları yazdırılacaktır</li>
                                <li>Her QR kod ayrı bir kartta gösterilecektir</li>
                                <li>Yazdırma öncesi sayfa yönlendirmesini kontrol edin</li>
                                <li>QR kodları kesip kapılara yapıştırabilirsiniz</li>
                            </ul>
                        </Text>
                    </div>
                </Card>
            </div>
        </LayoutComponent>
    );
};

export default TargetLocationQrPrintPage;
